import { Dialog } from "primereact/dialog";
import { useFormik } from "formik";

import type { LeaveTypeEntityT } from '../domain/entities/leaveType.entity';

import { ColaboraButton } from "components/v2";
import { FormStepOne, FormStepTwo } from "./Steps";

import { LEAVE_TYPES_STRINGS } from "../constants/strings";
import { normalizeIcon } from "../constants/leaveIconMapper";
import { useMultiStepDialog } from "hooks/ui/useMultiStepDialog";

import { validationSchema } from "../validationSchema";

const ALL_STEPS = [
  { id: "step-one", Component: FormStepOne },
  { id: "step-two", Component: FormStepTwo },
];

const STEP_ONE_FIELDS = [
  "isJustified",
  "code",
  "name",
  "incidenceTypeId",
  "icon",
];

const STEP_TWO_FIELDS = [
  "approvalFlow",
  "hasBalanceControl",
  "annualDays",
  "maxDaysPerRequest",
  "resetMonth",
  "requiresAttachment",
  "advanceNoticeRequirement",
  "minAdvanceNoticeDays",
  "allowBackdateDays",
  "maxBackdateDays",
];

type LeaveTypeDialogT = {
  visible: boolean;
  onHide: () => void;
  onSubmit: (payload: unknown) => void;
  activeType?: LeaveTypeEntityT | null;
  isSaving?: boolean;
};

const LeaveTypeDialog = ({
  visible,
  activeType = null,
  onHide,
  onSubmit,
  isSaving = false,
}: LeaveTypeDialogT) => {
  const initialValues = {
    // Step 1
    isJustified: activeType?.isJustified ?? false,
    code: activeType?.code ?? "",
    name: activeType?.name ?? "",
    description: activeType?.description ?? "",
    color: activeType?.color ?? "",
    incidenceTypeId: activeType?.incidence?.id ?? "",
    icon: activeType?.icon ? normalizeIcon(activeType?.icon) : "",
    // Step 2 (policy)
    approvalFlow: activeType?.policy?.approvalFlow ?? "leader_rrhh",
    hasBalanceControl: activeType?.policy?.hasBalanceControl ? "yes" : "no",
    annualDays: activeType?.policy?.annualDays ?? null,
    maxDaysPerRequest: activeType?.policy?.maxDaysPerRequest ?? null,
    resetMonth: activeType?.policy?.resetMonth ?? 1,
    requiresAttachment: activeType?.policy?.requiresAttachment ? "yes" : "no",
    advanceNoticeRequirement: activeType?.policy?.minAdvanceNoticeDays
      ? "required"
      : "none",
    minAdvanceNoticeDays: activeType?.policy?.minAdvanceNoticeDays || null,
    allowBackdateDays: activeType?.policy?.maxBackdateDays ? "yes" : "no",
    maxBackdateDays: activeType?.policy?.maxBackdateDays || null,
    maxAdvanceDays: activeType?.policy?.maxAdvanceDays ?? 0,
  };

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
    validateForm,
    setTouched,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: false,
    onSubmit: (formValues) => {
      const payload = validationSchema.cast(formValues, { stripUnknown: true });

      onSubmit(payload);
    },
  });

  const steps = values.isJustified ? ALL_STEPS.slice(0, 1) : ALL_STEPS;

  const { activeStep, isFirstStep, isLastStep, goToNext, goBack, reset } =
    useMultiStepDialog({ totalSteps: steps.length, onReset: resetForm });

  const handleOnNext = () =>
    goToNext(async () => {
      const formErrors = await validateForm();
      const hasStepErrors = STEP_ONE_FIELDS.some((f) => formErrors[f]);
      if (hasStepErrors) {
        setTouched(Object.fromEntries(STEP_ONE_FIELDS.map((f) => [f, true])));
        return false;
      }
      return true;
    });

  const handleOnSave = async () => {
    const formErrors = await validateForm();
    const fieldsToCheck = isFirstStep ? STEP_ONE_FIELDS : STEP_TWO_FIELDS;
    const hasStepErrors = fieldsToCheck.some((f) => formErrors[f]);

    if (hasStepErrors) {
      setTouched(Object.fromEntries(fieldsToCheck.map((f) => [f, true])));
      return;
    }

    handleSubmit();
  };

  const handleOnHide = () => {
    reset();
    onHide();
  };

  const formikProps = {
    values,
    errors,
    handleChange,
    setFieldValue,
    isCustom: activeType?.isCustom,
  };

  const currentStepFields = isFirstStep ? STEP_ONE_FIELDS : STEP_TWO_FIELDS;

  const isSaveDisabled = currentStepFields.some((f) => errors[f]) || isSaving;

  return (
    <Dialog
      header={
        activeType
          ? LEAVE_TYPES_STRINGS.DIALOG_TITLE_EDIT
          : LEAVE_TYPES_STRINGS.DIALOG_TITLE_NEW
      }
      visible={visible}
      style={{ width: "90vw", maxWidth: "50rem" }}
      onHide={handleOnHide}
      footer={
        <div className="flex justify-end gap-1">
          <ColaboraButton
            className="!w-[8rem]"
            label={
              isFirstStep
                ? LEAVE_TYPES_STRINGS.BUTTON_CANCEL
                : LEAVE_TYPES_STRINGS.BUTTON_BACK
            }
            variant="secondary"
            onClick={isFirstStep ? handleOnHide : goBack}
          />
          <ColaboraButton
            className="!w-[8rem]"
            label={
              isLastStep
                ? LEAVE_TYPES_STRINGS.BUTTON_SAVE
                : LEAVE_TYPES_STRINGS.BUTTON_CONTINUE
            }
            onClick={isLastStep ? handleOnSave : handleOnNext}
            disabled={isSaveDisabled}
            isLoading={isSaving}
          />
        </div>
      }
    >
      <form className="overflow-hidden">
        <div
          className="grid transition-transform duration-300"
          style={{
            gridTemplateColumns: `repeat(${steps.length}, 100%)`,
            transform: `translateX(${activeStep * -100}%)`,
          }}
        >
          {steps.map(({ id, Component }) => (
            <Component key={id} {...formikProps} />
          ))}
        </div>
      </form>
    </Dialog>
  );
};

export default LeaveTypeDialog;
